import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Terminal } from "lucide-react";
import { api, seedSelectSQL, type SchemaView } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * Read-only inspect path for a PostgreSQL view: definition + optional sample rows
 * via SQL query (Data API is table-oriented; views are browsed read-only here).
 */
export function ViewViewerPage() {
  const { id = "", view = "" } = useParams<{ id: string; view: string }>();
  const [meta, setMeta] = useState<SchemaView | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [rowError, setRowError] = useState<unknown>(null);

  useEffect(() => {
    if (!id || !view) return;
    void (async () => {
      setLoading(true);
      setError(null);
      setRowError(null);
      try {
        const { data } = await api.describeView(id, view);
        setMeta(data);
        try {
          const q = await api.sqlQuery(id, seedSelectSQL(view, 50));
          setColumns(q.data.columns ?? []);
          setRows(q.data.rows ?? []);
        } catch (e) {
          setRowError(e);
          setRows([]);
          setColumns([]);
        }
      } catch (e) {
        setError(e);
        setMeta(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, view]);

  if (loading) return <Spinner label="Loading view…" />;

  return (
    <div>
      <PageHeader
        eyebrow="View"
        title={view}
        description="Read-only. Sample rows via SQL query in the project schema."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">view</Badge>
            <Button variant="outline" size="sm" asChild>
              <Link
                to={`/dashboard/projects/${id}/sql?relation=${encodeURIComponent(view)}&kind=view`}
              >
                <Terminal className="mr-1.5 h-3.5 w-3.5" />
                Open in SQL
              </Link>
            </Button>
          </div>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      {meta ? (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Definition</CardTitle>
              <CardDescription>
                {meta.columns.length} column
                {meta.columns.length !== 1 ? "s" : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {meta.columns.map((c) => (
                  <span key={c.name} className="mono-chip !text-[11px]">
                    {c.name}
                    <span className="text-text-disabled"> · {c.udt_name || c.data_type}</span>
                  </span>
                ))}
              </div>
              <pre className="overflow-x-auto rounded-lg border border-code-block-border bg-code-block-bg p-3 font-mono text-xs text-code-block-text">
                {meta.definition || "(no definition)"}
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Sample rows</CardTitle>
              <CardDescription>Up to 50 rows via SELECT *</CardDescription>
            </CardHeader>
            <CardContent>
              {rowError ? (
                <ErrorBox error={rowError} />
              ) : rows.length === 0 ? (
                <p className="text-sm text-text-muted">No rows returned.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        {(columns.length
                          ? columns
                          : Object.keys(rows[0] ?? {})
                        ).map((c) => (
                          <th key={c}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i}>
                          {(columns.length
                            ? columns
                            : Object.keys(row)
                          ).map((c) => (
                            <td key={c}>{formatCell(row[c])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v == null) return "null";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
